import type { V2BadgeTone } from '@sakhtar/design-system/react';
import { ApiError } from '../../../lib/apiClient.js';
import { faDigits } from '../../../lib/faDigits.js';
import { toEnglishDigits } from '../../../lib/phoneNumber.js';
import type { LeadPriority, LeadStatus } from '../../../lib/types/crm.js';

/** Badge tone per status — the label always carries the meaning, not colour alone. */
export const LEAD_STATUS_TONE: Record<LeadStatus, V2BadgeTone> = {
  new: 'neutral',
  contacted: 'info',
  qualified: 'info',
  converted: 'success',
  disqualified: 'danger',
};

/** Quick views above the list (Sales CRM spec: همه / باز / من / نیازمند پیگیری / …). */
export const LEAD_QUICK_VIEWS = [
  { id: 'all', label: 'همه' },
  { id: 'open', label: 'در جریان' },
  { id: 'mine', label: 'سرنخ‌های من' },
  { id: 'followup', label: 'نیازمند پیگیری' },
  { id: 'converted', label: 'برنده‌شده' },
  { id: 'disqualified', label: 'از دست رفته' },
] as const;
export type LeadQuickView = (typeof LEAD_QUICK_VIEWS)[number]['id'];

export const LEAD_PRIORITY_TONE: Record<LeadPriority, V2BadgeTone> = {
  low: 'neutral',
  normal: 'info',
  high: 'warn',
  urgent: 'danger',
};

/** Statuses that are still being worked — the only ones with a stage to move. */
export function isOpenLead(status: LeadStatus): boolean {
  return status === 'new' || status === 'contacted' || status === 'qualified';
}

const FIELD_MESSAGES: Record<string, string> = {
  leadKind: 'نوع سرنخ را انتخاب کنید.',
  personFirstName: 'نام را وارد کنید.',
  personLastName: 'نام خانوادگی را وارد کنید.',
  organizationName: 'نام مجموعه را وارد کنید.',
  phone: 'شمارهٔ تلفن کامل نیست.',
  altContactKind: 'نوع راه ارتباطی را انتخاب کنید.',
  altContactValue: 'نشانی ایمیل معتبر نیست.',
  needSubject: 'موضوع نیاز را بنویسید.',
  sourceLabel: 'منبع را انتخاب کنید.',
  sourceDetail: 'برای این منبع توضیح لازم است.',
  ownerMemberRef: 'مسئول انتخاب‌شده معتبر نیست یا به سرنخ‌ها دسترسی ندارد.',
  nextFollowUpAt: 'زمان پیگیری معتبر نیست.',
  reason: 'دلیل رد را بنویسید.',
  amountRial: 'مبلغ باید عدد صحیح و نامنفی (ریال) باشد.',
  expectedCloseDate: 'تاریخ احتمالی بستن معتبر نیست.',
  stageRef: 'مرحلهٔ انتخاب‌شده معتبر نیست.',
  partyRef: 'مشتری انتخاب‌شده پیدا نشد یا مشتری نیست.',
  lostReasonCode: 'دلیل از دست رفتن را انتخاب کنید.',
  note: 'برای «سایر» توضیح بنویسید.',
  nextActionTitle: 'برای موعد اقدام بعدی، عنوان آن را بنویسید.',
  nextActionDueAt: 'موعد اقدام بعدی معتبر نیست.',
  productRefs: 'کالای انتخاب‌شده معتبر نیست.',
};

/** Server `details[]` → Persian message per form field. */
export function fieldErrorsFrom(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};
  const errors: Record<string, string> = {};
  for (const detail of error.envelope.error.details ?? []) {
    if (detail.path === 'mobile') {
      errors['mobile'] = detail.message.includes('at least')
        ? 'دست‌کم یک راه ارتباطی (موبایل، تلفن ثابت یا راه دیگر) لازم است.'
        : detail.message.includes('Duplicate')
          ? 'این شماره قبلاً برای سرنخ دیگری ثبت شده است.'
          : 'شمارهٔ همراه معتبر نیست.';
    } else if (FIELD_MESSAGES[detail.path]) {
      errors[detail.path] = FIELD_MESSAGES[detail.path] as string;
    }
  }
  return errors;
}

export function detailValue(error: unknown, path: string): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.envelope.error.details?.find((d) => d.path === path)?.message || null;
}

/** Same normalisation as the server: Persian digits, spaces, +98. */
export function normalizeMobileInput(value: string): string | null {
  let digits = toEnglishDigits(value).replace(/\D/g, '');
  if (digits.startsWith('0098')) digits = `0${digits.slice(4)}`;
  else if (digits.startsWith('98') && digits.length === 12) digits = `0${digits.slice(2)}`;
  else if (digits.startsWith('9') && digits.length === 10) digits = `0${digits}`;
  return /^09\d{9}$/.test(digits) ? digits : null;
}

/** Two-letter initials for the detail header avatar. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return second ? `${first}.${second}` : first;
}

/**
 * A masked number for display: Persian digits, and the server's `···`
 * shown as spaced bullets — a middle dot next to Persian digits reads as
 * «۰» (Persian zero is itself a dot), which made «۰۹۱۲···۴۵۶۷» look real.
 */
export function formatMasked(value: string): string {
  return faDigits(value).replace('···', ' ••• ');
}
